// One person an invoice involves: who receives it, or who signs it off.
//
// The owner (a partner or a deal) is not on the form. A contact is added from
// its owner's page and stays there: `partner_id` and `deal_id` are immutable,
// so they are sent on create and never again.

import type { ReactNode } from "react";
import { BillingContactCreate, BillingContactUpdate } from "@crm/shared";
import type { BillingContact, BillingContactRole } from "@crm/shared";
import type { BillingContactOwner } from "../../api/queries.js";
import { useBillingContactMutations } from "../../api/mutations.js";
import { FormDialog } from "../../forms/FormDialog.js";
import { SelectField, TextField, type SelectOption } from "../../forms/fields.js";
import { optionalText } from "../../forms/parseInput.js";
import { useEditor, type Wire } from "../../forms/useEditor.js";
import { billingContactRoleLabel } from "../../format/labels.js";

interface ContactDraft {
  role: string;
  name: string;
  email: string;
}

const LABELS = {
  role: "Role",
  name: "Name",
  email: "Email",
} as const;

const ROLES: readonly BillingContactRole[] = ["recipient", "approver"];
const ROLE_OPTIONS: readonly SelectOption<BillingContactRole>[] = ROLES.map((role) => ({
  value: role,
  label: billingContactRoleLabel(role),
}));

function draftFromRecord(contact: BillingContact): ContactDraft {
  return { role: contact.role, name: contact.name ?? "", email: contact.email };
}

export interface BillingContactFormDialogProps {
  owner: BillingContactOwner;
  /** The partner or customer the contact is for, named in the dialog. */
  ownerName: string;
  /** Absent when adding a contact. */
  contact?: BillingContact;
  onClose: () => void;
}

export function BillingContactFormDialog({
  owner,
  ownerName,
  contact,
  onClose,
}: BillingContactFormDialogProps): ReactNode {
  const mutations = useBillingContactMutations();
  const isNew = contact === undefined;

  const toWire = (draft: ContactDraft): Wire => ({
    // Only a create names the owner: it is absent from the update schema.
    ...(isNew ? (owner.kind === "partner" ? { partner_id: owner.id } : { deal_id: owner.id }) : {}),
    role: draft.role,
    name: optionalText(draft.name),
    email: draft.email.trim(),
  });

  const editor = useEditor<
    ContactDraft,
    BillingContactCreate,
    BillingContactUpdate,
    BillingContact
  >({
    mode: contact === undefined ? { kind: "create" } : { kind: "update", record: contact },
    blankDraft: { role: "recipient", name: "", email: "" },
    draftFromRecord,
    toWire,
    createSchema: BillingContactCreate,
    updateSchema: BillingContactUpdate,
    create: (body, idempotencyKey) => mutations.create.mutateAsync({ body, idempotencyKey }),
    update: (id, body, version) => mutations.update.mutateAsync({ id, body, version }),
    fetchCurrent: mutations.fetchCurrent,
    onSaved: onClose,
  });

  return (
    <FormDialog
      open
      onClose={onClose}
      title={isNew ? "Add a billing contact" : "Edit billing contact"}
      description={
        owner.kind === "partner"
          ? `A person ${ownerName}'s invoices involve. Add as many as there are. A deal can name its own instead.`
          : `A person ${ownerName}'s invoices involve. Once this deal has anyone of its own in a role, its partner's people in that role are no longer used for it.`
      }
      submitLabel={isNew ? "Add contact" : "Save changes"}
      editor={editor}
      labels={LABELS}
    >
      <SelectField
        label={LABELS.role}
        required
        options={ROLE_OPTIONS}
        value={editor.draft.role}
        error={editor.errors["role"]}
        onChange={(value) => editor.setField("role", value)}
      />
      <TextField
        label={LABELS.name}
        value={editor.draft.name}
        error={editor.errors["name"]}
        onChange={(value) => editor.setField("name", value)}
      />
      <TextField
        label={LABELS.email}
        required
        value={editor.draft.email}
        error={editor.errors["email"]}
        onChange={(value) => editor.setField("email", value)}
      />
    </FormDialog>
  );
}
